import { useState, useEffect, useCallback } from 'react';
import { ZenitalCamera } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { captureException } from '../utils/captureException';

interface UseZenitalCameraResult {
  zenitalCamera: ZenitalCamera | null;
  loading: boolean;
  error: string | null;
}

export const useZenitalCamera = (): UseZenitalCameraResult => {
  const [zenitalCamera, setZenitalCamera] = useState<ZenitalCamera | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchZenitalCamera = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${process.env.REACT_APP_API_URL || ''}/api/zenital/camera`;
      const idToken = await user?.getIdToken();
      
      if (!idToken) {
        throw new Error('No idToken found');
      }
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setZenitalCamera(data);
    } catch (err) {
      captureException(err, { tags: { feature: 'useZenitalCamera' }, extra: { action: 'fetchZenitalCamera' } });
      console.error('Error fetching zenital camera:', err);
      setError(err instanceof Error ? err.message : 'Error al connectar con la cámara Zenital. Estás conectado a la red wifi?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchZenitalCamera();
  }, [fetchZenitalCamera]);

  return {
    zenitalCamera,
    loading,
    error,
  };
};
